import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ExperimentService } from 'src/app/_services/experiment.service';
import { DeviceService } from 'src/app/_services/device.service';
import { Experiment } from 'src/app/_models/experiment';
import { ModalService } from 'src/app/_services/modal.service';
import { Device } from 'src/app/_models/device';
import { ErrorObject } from 'src/app/_models/error';
import { ExperimentStatus } from 'src/app/_models/experiment-status';
import { ExperimentMode } from 'src/app/_models/experiment-mode';
import { Subject, Subscription } from 'rxjs';
import { DeviceSetupStatus } from 'src/app/_models/device-setup-status';
import { LAB_ADMIN_ROLE_LIST, Role } from 'src/app/_models/role';
import { User } from 'src/app/_models/user';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { ModalComponent } from '../../_components/modals/modal.component';
import { LoadingSpinnerComponent } from '../../_components/loading-spinner/loading-spinner.component';
import { DeviceSetupRowComponent } from '../../_components/device-setup-row/device-setup-row.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { Laboratory } from 'src/app/_models/laboratory';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { StatusIndicatorComponent } from 'src/app/_components/chips/status-indicator/status-indicator.component';
import { IconButtonComponent } from 'src/app/_components/buttons/icon-button/icon-button.component';

@Component({
    selector: 'app-device-setup',
    templateUrl: './device-setup.component.html',
    styleUrls: ['./device-setup.component.scss'],
    imports: [
        ButtonComponent,
        DeviceSetupRowComponent,
        LoadingSpinnerComponent,
        ModalComponent,
        ValidationModalComponent,
        RoleCheckPipe,
        ErrorPageComponent,
        MatIconButtonComponent,
        StatusIndicatorComponent,
        IconButtonComponent,
    ]
})
export class DeviceSetupComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly modalService = inject(ModalService);
  private readonly experimentService = inject(ExperimentService);
  private readonly deviceService = inject(DeviceService);
  private readonly authenticationService = inject(AuthenticationService);
  private deviceSubscription: Subscription;

  // Control vars
  getExperimentError: ErrorObject = null;
  deviceError: ErrorObject = null;
  deleteError: ErrorObject = null;
  // Handles update device/start experiment errors
  error: ErrorObject = null;

  pageIsLoading = true;
  isProcessing = false;
  showMorbidostatWarning = signal(false);
  deleteIsProcessing = false;
  startExperimentButtonDisabled = true;
  allowNavigation: Subject<boolean> = new Subject<boolean>();

  ExperimentStatus = ExperimentStatus;
  DeviceSetupStatus = DeviceSetupStatus;
  Role = Role;
  readonly labAdminRoles = LAB_ADMIN_ROLE_LIST;
  laboratory: Laboratory;

  /**
   *  @param {Experiment} experiment The experiment
   */
  experiment: Experiment = null;

  /**
   *  @param {number} experimentId The experiment id
   */
  experimentId: number;

  devices: Device[]; // List of devices assigned to the experiment
  currentUser: User;
  deviceIdList: number[] = [];

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      this.experimentId = parseInt(params.id as string, 10);
    });

    this.currentUser = this.authenticationService.currentUserValue;
    this.laboratory = this.authenticationService.selectedLaboratory;

    this.getExperimentFromApi(this.experimentId);
  }

  getExperimentFromApi(experimentId: number): void {
    this.pageIsLoading = true;
    this.getExperimentError = null;
    this.deviceError = null;
    this.experimentService.getExperimentById(experimentId).subscribe({
      next: (response) => {
        this.experiment = response;
        this.showMorbidostatWarning.set(
          response.experimentMode === ExperimentMode.Morbidostat,
        );
        this.pageIsLoading = false;
        switch (this.experiment.experimentStatusId) {
          case ExperimentStatus.DraftLocked:
            if (this.experiment.devices.length === 0) {
              this.deviceError = {
                message:
                  'No devices are assigned to this experiment. Please contact support.',
                status: 400,
                code: null,
              };
              return;
            }
            this.initialiseDevices(this.experiment);
            break;
          case ExperimentStatus.Active:
            this.deviceError = {
              message:
                'This experiment is already active. Please return to dashboard.',
              status: 400,
              code: null,
            };
            return;
          case ExperimentStatus.Complete:
            this.deviceError = {
              message:
                'This experiment is already complete. Please return to dashboard.',
              status: 400,
              code: null,
            };
            break;
          default:
            break;
        }
      },
      error: (error: ErrorObject) => {
        this.getExperimentError = error;
        this.pageIsLoading = false;
      },
    });
  }

  initialiseDevices(experiment: Experiment): void {
    experiment.devices.forEach((device) => {
      this.deviceIdList.push(device.id);
    });
    this.startPolling();
  }

  startPolling(): void {
    this.deviceIdList = [];
    this.experiment.devices.forEach((device) => {
      this.deviceIdList.push(device.id);
    });
    this.deviceSubscription = this.deviceService
      .startDevicePolling(this.experiment.id, this.deviceIdList)
      .subscribe(({ experiment, devices }) => {
        // If any devices have been removed, restart polling (with updated device list)
        if (experiment.devices.length !== devices.length) {
          this.restartPolling();
        }
        switch (experiment.experimentStatusId) {
          case ExperimentStatus.Active:
          case ExperimentStatus.Complete:
          case ExperimentStatus.Paused:
            this.stopPolling();
            void this.router.navigate([
              `/experiment/${this.experimentId}/display`,
            ]);
            return;
          case ExperimentStatus.Draft:
            this.stopPolling();
            void this.router.navigate([
              `/experiment/${this.experimentId}/select-device`,
            ]);
            return;
          case ExperimentStatus.Cancelled:
            this.stopPolling();
            void this.router.navigate(['/dashboard']);
            return;
          default:
            break;
        }

        this.experiment = experiment;
        this.devices = devices;
        this.pageIsLoading = false;

        const devicesReady = this.devices.filter(
          (x) => x.deviceStatusId === DeviceSetupStatus.FlasksInserted,
        );
        if (devicesReady.length === this.devices.length) {
          this.startExperimentButtonDisabled = false;
          this.stopPolling();
        }
      });
  }

  restartPolling(): void {
    this.stopPolling();
    this.startPolling();
  }

  confirmFlaskIsInserted(deviceId: string): void {
    this.error = null;
    this.stopPolling();

    const deviceStatusId = DeviceSetupStatus.FlasksInserted;
    // api call - update device status
    this.deviceService
      .updateDeviceStatus(deviceId, { deviceStatusId })
      .subscribe({
        next: () => {
          // Update the device status
          const device = this.devices.find(
            (x) => x.id === parseInt(deviceId, 10),
          );
          device.deviceStatusId = deviceStatusId;
          // If all devices are ready, enable start experiment button
          const devicesReady = this.devices.filter(
            (x) => x.deviceStatusId === DeviceSetupStatus.FlasksInserted,
          );
          if (devicesReady.length === this.devices.length) {
            this.startExperimentButtonDisabled = false;
          } else {
            this.startExperimentButtonDisabled = true;
            this.startPolling();
          }
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.openModal('error');
        },
      });
  }

  startExperiment(): void {
    if (this.experiment?.experimentMode === ExperimentMode.Morbidostat) {
      this.openModal('morbidostat-warning');
      return;
    }
    this.doStartExperiment();
  }

  private doStartExperiment(): void {
    // If form is already being submitted, exit
    if (this.isProcessing) {
      return;
    }

    this.stopPolling();

    this.isProcessing = true;
    this.error = null;
    this.experimentService.startExperiment(this.experiment.id).subscribe({
      next: () => {
        this.isProcessing = false;
        this.openModal('experiment-started');
      },
      error: (error: ErrorObject) => {
        this.error = error;
        this.isProcessing = false;
        this.openModal('error');
      },
    });
  }

  deleteDraft(): void {
    // If request has already been submitted, exit
    if (this.isProcessing || this.deleteIsProcessing) {
      return;
    }

    this.stopPolling();

    this.deleteError = null;
    this.deleteIsProcessing = true;

    this.experimentService.deleteExperimentById(this.experimentId).subscribe({
      next: () => {
        this.deleteIsProcessing = false;
        this.closeModal('delete-draft');
        this.openModal('delete-success');
      },
      error: (error: ErrorObject) => {
        this.deleteIsProcessing = false;
        this.deleteError = error;
        this.closeModal('delete-draft');
        this.openModal('delete-error');
        this.startPolling();
      },
    });
  }

  goBack(): void {
    // Route to the set params page
    void this.router.navigate([
      `experiment/${this.experimentId}/select-device`,
    ]);
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  stopPolling(): void {
    if (this.deviceSubscription) {
      this.deviceSubscription.unsubscribe();
    }
    this.deviceService.stopDevicePolling();
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'dashboard-btn':
        void this.router.navigate(['/dashboard']);
        break;
      case 'view-exp-btn':
        void this.router.navigate([`/experiment/${this.experimentId}/display`]);
        break;
      case 'morbidostat-cancel':
        this.closeModal('morbidostat-warning');
        break;
      case 'morbidostat-confirm':
        this.closeModal('morbidostat-warning');
        this.doStartExperiment();
        break;
      case 'close-button':
        this.error = null;
        this.closeModal('error');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.stopPolling();
        this.error = null;
        this.closeModal('attention');
        break;
      case 'delete-success-button':
        this.closeModal('delete-success');
        this.allowNavigation.next(true);
        void this.router.navigate(['dashboard']);
        break;
      case 'error-primary-button':
        this.error = null;
        this.closeModal('delete-error');
        break;
      case 'error-secondary-button':
        this.error = null;
        this.allowNavigation.next(true);
        this.closeModal('delete-error');
        void this.router.navigate(['dashboard']);
        break;
      default:
        break;
    }
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  // Ensure we always stop polling when navigating away
  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.stopPolling();
      resolve(true);
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
